// The timed UI shapes product code repeats, each once, on the product timer
// module (#12187): a debounced value, a flash that clears itself, a ticker,
// and a typewriter reveal. Every timer is cleared on unmount and every one
// runs through `setProductTimeout` / `setProductInterval`, so a story drives
// it with `withProductClock` instead of waiting real time. The debounced
// callback lives in ./useDebouncedCallback.

import { useCallback, useEffect, useState } from "react";
import {
  clearProductInterval,
  clearProductTimeout,
  setProductInterval,
  setProductTimeout,
} from "@/utils/productTimers";

/** `value`, once it has held still for `ms`. */
export const useDebouncedValue = <T>(value: T, ms: number): T => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setProductTimeout(() => setSettled(value), ms);
    return () => clearProductTimeout(timer);
  }, [value, ms]);
  return settled;
};

/**
 * A flag that turns itself off `ms` after it was last turned on: the
 * "Copied" tick, a row highlight, a saved confirmation.
 */
export const useFlash = (ms: number): [boolean, () => void] => {
  const [on, setOn] = useState(false);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const timer = setProductTimeout(() => setOn(false), ms);
    return () => clearProductTimeout(timer);
  }, [on, generation, ms]);
  const trigger = useCallback(() => {
    setOn(true);
    setGeneration((g) => g + 1);
  }, []);
  return [on, trigger];
};

/**
 * A count that goes up by one every `ms` while `active`: a relative-time
 * label, an elapsed counter. Stops, and holds its value, when inactive.
 */
export const useTicker = (ms: number, active = true): number => {
  const [ticks, setTicks] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setProductInterval(() => setTicks((t) => t + 1), ms);
    return () => clearProductInterval(timer);
  }, [ms, active]);
  return ticks;
};

export interface TypewriterOptions {
  /** Characters revealed per step. */
  charsPerStep: number;
  /** Time between steps. */
  stepMs: number;
  /** Show the whole text at once (reduced motion, or text already seen). */
  instant?: boolean;
}

/**
 * The prefix of `text` revealed so far. Text that grows (a stream) keeps its
 * revealed prefix and continues from there; text that changes to something
 * else starts again.
 */
export const useTypewriter = (
  text: string,
  { charsPerStep, stepMs, instant = false }: TypewriterOptions
): string => {
  const [shown, setShown] = useState(instant ? text.length : 0);
  const [source, setSource] = useState(text);
  if (source !== text) {
    setSource(text);
    if (!text.startsWith(source)) setShown(instant ? text.length : 0);
  }
  const done = instant || shown >= text.length;
  useEffect(() => {
    if (done) return undefined;
    const timer = setProductInterval(
      () => setShown((n) => Math.min(text.length, n + charsPerStep)),
      stepMs
    );
    return () => clearProductInterval(timer);
  }, [done, text.length, charsPerStep, stepMs]);
  return instant ? text : text.slice(0, shown);
};
